// @ts-check
import { Box, Link, Text } from '~/design-system';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useLocation } from 'react-router-dom';
import ChooseFile, {
  useChooseFile,
} from '../../../components/ChooseFile/ChooseFile';
import Footer, {
  BackButton,
  CancelButton,
  NextButton,
} from '../../../components/Layout/Footer';
import Layout from '../../../components/Layout/Layout';
import Main from '../../../components/Layout/Main';
import {
  PageHeader,
  PageSubtitle,
  PageTitle,
} from '../../../components/Layout/PageHeader';
import {
  ProgressIndicator,
  useLink,
  importType,
  fieldsToImport,
} from './$importId/_config';
import FileRequirements, {
  FileFormatRow,
  RequirementRow,
  RequiredFieldsList,
} from '../../../components/FileRequirements/FileRequirements';

const optionalFields = fieldsToImport.filter((f) => !f.required);

/**
 * Renders the imports overview, import type selection and file upload
 */
export default function UploadFile() {
  const { i18n } = useLingui();
  const link = useLink('uploadFile');
  const query = new URLSearchParams(useLocation().search);
  /** @type {string | null}  */
  const collectionId = query.get('collectionId');
  const props = useChooseFile({
    importType,
    collectionId,
  });
  const optionalLabels = optionalFields.map((f) => i18n._(f.label)).join(', ');
  return (
    <Layout>
      <ProgressIndicator step="uploadFile" />
      <PageHeader>
        <PageTitle>
          <Trans>Upload your file</Trans>
        </PageTitle>
        <Box sx={{ textAlign: 'center' }}>
          <PageSubtitle>
            <Trans>
              Before you upload your file, make sure your it is{' '}
              <Link
                href="https://docs.google.com/document/d/1L75eP7uhwiC4ePEboV-p5ZJP_s8SzweqWD3rMLryweY/"
                target="_blank"
              >
                ready to be imported.
              </Link>
            </Trans>
          </PageSubtitle>
          <Text variant="default">
            <Trans>
              Learn how Bottlebooks knows when your records are{' '}
              <Link
                href="https://docs.google.com/document/d/1KHm5wbcLPhnn5pGp8bSsbdH5AWko5it521NOkXWM1rM/"
                target="_blank"
              >
                duplicates
              </Link>
              .
            </Trans>
          </Text>
        </Box>
      </PageHeader>
      <Main>
        <ChooseFile {...props} />
        <FileRequirements>
          <FileFormatRow />
          <RequirementRow
            label={<Trans>Required columns</Trans>}
            body={<RequiredFieldsList fields={fieldsToImport} />}
          />
          <RequirementRow
            label={<Trans>Optional columns</Trans>}
            body={optionalLabels}
            isLast
          />
        </FileRequirements>
      </Main>
      <Footer>
        <BackButton to={link.back()} />
        <CancelButton to={link.cancel()} />
        <NextButton
          to={link.next({ importId: props.importId })}
          disabled={!props.isValid}
        />
      </Footer>
    </Layout>
  );
}
